// SPDX-License-Identifier: GPL-3.0-or-later

import { detectFiles, openFile, type AppFiles } from '../fs/files';
import { downloadBlob } from '../fs/zip';
import { readJobTxt } from '../fs/jobtxt';
import {
  generateCover,
  generateCv,
  generateInterview,
  type DocKind,
  type GenerateOutcome,
} from '../generate';
import { removeApplication, updateStatus } from '../store/applications';
import {
  STATUSES,
  type Application,
  type Manifest,
  type Status,
} from '../types';
import { getWalletState } from '../wallet';
import { openDocEditor } from './DocEditor';
import { el } from './dom';

function isEasyApply(t: Application['applyType']): boolean {
  return typeof t === 'string' && t.toLowerCase().includes('easy');
}

function statusClass(s: Status): string {
  return s.toLowerCase();
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export interface JobDetailOptions {
  app: Application;
  manifest: Manifest;
  root: FileSystemDirectoryHandle;
}

async function downloadDoc(
  root: FileSystemDirectoryHandle | null,
  relPath: string,
): Promise<void> {
  if (!root) throw new Error('Folder is not currently mounted.');
  const parts = relPath.split('/').filter(Boolean);
  const name = parts.pop();
  if (!name) throw new Error('Invalid file path.');
  let cur: FileSystemDirectoryHandle = root;
  for (const seg of parts) cur = await cur.getDirectoryHandle(seg);
  const file = await (await cur.getFileHandle(name)).getFile();
  downloadBlob(file, name);
}

function renderGenerateSlot(
  app: Application,
  kind: DocKind,
  title: string,
  cta: string,
  description: string,
  runner: () => Promise<GenerateOutcome>,
  root: FileSystemDirectoryHandle | null,
  extra?: HTMLElement,
): HTMLElement {
  const status = el('div', { class: 'gen-status' });
  const errBox = el('pre', { class: 'error-box', style: 'display: none;' });
  const logBox = el('pre', { class: 'log-box', style: 'display: none;' });
  const hallucinationBox = el('div', { style: 'display: none; margin-top: 0.4rem;' });

  const existingGen = app.generated?.[kind];
  const hasDoc = !!existingGen?.texRelPath;

  // Generate / Regenerate share this. On success or a compile-stage failure the
  // store persists a ref → the whole JobDetail re-renders with the updated app,
  // which is what swaps the header from Generate to Edit/Download/Regenerate.
  const runGenerate = async (triggerBtn: HTMLButtonElement): Promise<void> => {
    const ws = getWalletState();
    if (!ws.connection) {
      status.innerHTML = 'Turn on AI in <a href="#/settings?focus=wallet">Settings</a> first.';
      return;
    }
    triggerBtn.disabled = true;
    status.textContent = 'Generating… AI 10–30 s, then Typst compile.';
    errBox.style.display = 'none';
    errBox.textContent = '';
    logBox.style.display = 'none';
    logBox.textContent = '';
    hallucinationBox.style.display = 'none';
    hallucinationBox.replaceChildren();
    try {
      const outcome = await runner();
      if (outcome.ok) {
        status.textContent = outcome.pdfFilename
          ? `Saved ${outcome.pdfFilename} (+ ${outcome.texFilename}).`
          : `Saved ${outcome.texFilename}. (PDF compile didn't run.)`;
        if (outcome.hallucinations && outcome.hallucinations.flagged.length > 0) {
          hallucinationBox.appendChild(renderHallucinations(outcome.hallucinations));
          hallucinationBox.style.display = 'block';
        }
      } else {
        status.textContent = `Failed at "${outcome.stage}": ${outcome.message}`;
        if (outcome.raw) {
          errBox.textContent = outcome.raw;
          errBox.style.display = 'block';
        }
        if (outcome.log) {
          logBox.textContent = lastLines(outcome.log, 50);
          logBox.style.display = 'block';
        }
      }
    } catch (err) {
      status.textContent = `Unexpected: ${err instanceof Error ? err.message : String(err)}`;
    } finally {
      triggerBtn.disabled = false;
    }
  };

  // All actions live in one place: the card header (right side).
  const headActions = el('div', { class: 'gen-actions' });
  if (hasDoc && existingGen) {
    const texRelPath = existingGen.texRelPath!;
    const pdfRelPath = existingGen.pdfRelPath;

    const editBtn = el(
      'button',
      {
        class: 'btn tiny primary',
        title: 'Open the editor to preview, edit, or ask AI for changes.',
        onclick: () => {
          if (!root) {
            alert('Folder is not currently mounted.');
            return;
          }
          openDocEditor({
            app,
            kind,
            root,
            texRelPath,
            ...(pdfRelPath ? { pdfRelPath } : {}),
          });
        },
      },
      'Edit & refine',
    );

    const downloadBtn = el(
      'button',
      {
        class: 'btn tiny',
        disabled: pdfRelPath ? null : true,
        title: pdfRelPath
          ? 'Download the PDF'
          : 'PDF not ready — open Edit & refine to see the compile error.',
        onclick: pdfRelPath
          ? async () => {
              try {
                await downloadDoc(root, pdfRelPath);
              } catch (err) {
                alert(`Could not download: ${err instanceof Error ? err.message : String(err)}`);
              }
            }
          : undefined,
      },
      'Download',
    );

    const regenBtn = el(
      'button',
      { class: 'btn tiny ghost', title: 'Generate a fresh version from scratch.' },
      'Regenerate',
    ) as HTMLButtonElement;
    regenBtn.addEventListener('click', () => { void runGenerate(regenBtn); });

    headActions.append(editBtn, downloadBtn, regenBtn);
  } else {
    const genBtn = el('button', { class: 'btn primary compact' }, cta) as HTMLButtonElement;
    genBtn.addEventListener('click', () => { void runGenerate(genBtn); });
    headActions.append(genBtn);
  }

  const meta = existingGen
    ? el(
        'div',
        { class: 'text-faint', style: 'font-size: 0.68rem; letter-spacing: 0.04em;' },
        `Last ${new Date(existingGen.generatedAt).toLocaleString()} · ${existingGen.pdfRelPath ? 'PDF + .typ' : '.typ only'}`,
      )
    : null;

  // Consistent ordering across all slot kinds: title → description → form
  // (if any) → actions. Buttons land as the final step regardless of whether
  // the slot has form inputs.
  const container = el(
    'div',
    { class: 'gen-slot' },
    el('div', { class: 'gen-title' }, title),
    el('div', { class: 'text-muted', style: 'font-size: 0.72rem; line-height: 1.55;' }, description),
  );
  if (extra) container.append(extra);
  container.append(headActions);
  container.append(status, errBox, logBox, hallucinationBox);
  if (meta) container.append(meta);
  return container;
}

function renderHallucinations(
  report: import('../ai/hallucination').HallucinationReport,
): HTMLElement {
  const summary = el(
    'summary',
    {},
    `Review ${report.flagged.length} of ${report.totalBullets} bullets — claims not found in your profile`,
  );
  const items = el('div', { style: 'display: flex; flex-direction: column; gap: 0.45rem; margin-top: 0.4rem;' });
  for (const f of report.flagged) {
    items.appendChild(
      el(
        'div',
        { class: 'hallucination-item' },
        el('div', { class: 'role' }, `${f.role}${f.role ? ' · ' : ''}${f.company}`),
        el('div', { class: 'bullet' }, f.bullet),
        el(
          'div',
          { style: 'display: flex; flex-wrap: wrap; gap: 0.3rem;' },
          ...f.tokens.map((t) =>
            el('span', { class: 'hallucination-token' }, t.token),
          ),
        ),
      ),
    );
  }
  return el('details', { class: 'hallucination-box' }, summary, items);
}

function lastLines(s: string, n: number): string {
  const lines = s.split(/\r?\n/);
  return lines.slice(Math.max(0, lines.length - n)).join('\n');
}

function renderInterviewSlot(app: Application, root: FileSystemDirectoryHandle | null): HTMLElement {
  const nameInput = el('input', {
    type: 'text',
    placeholder: 'Interviewer name',
    class: 'settings-input',
    style: 'font-size: 0.78rem; padding: 0.45rem 0.65rem;',
  });
  const roleInput = el('input', {
    type: 'text',
    placeholder: 'Role (optional)',
    class: 'settings-input',
    style: 'font-size: 0.78rem; padding: 0.45rem 0.65rem;',
  });
  const typeSelect = el(
    'select',
    { class: 'settings-input', style: 'font-size: 0.78rem; padding: 0.45rem 0.65rem;' },
    el('option', { value: '' }, 'Interview type (auto)'),
    el('option', { value: 'screening' }, 'Screening'),
    el('option', { value: 'technical' }, 'Technical'),
    el('option', { value: 'behavioral' }, 'Behavioural'),
    el('option', { value: 'panel' }, 'Panel'),
    el('option', { value: 'culture' }, 'Culture'),
    el('option', { value: 'final' }, 'Final'),
  );
  const inputs = el(
    'div',
    { style: 'display: grid; grid-template-columns: 1fr 1fr; gap: 0.4rem; margin-top: 0.25rem;' },
    nameInput, roleInput, typeSelect,
  );
  return renderGenerateSlot(
    app,
    'interview',
    'Interview prep',
    'Generate',
    'Tailored briefing on the interviewer, expected questions, and answers grounded in your profile.',
    async () => {
      const name = nameInput.value.trim();
      if (!name) {
        return {
          ok: false, kind: 'interview', stage: 'state',
          message: 'Enter the interviewer name first.',
        };
      }
      const role = roleInput.value.trim();
      const type = typeSelect.value.trim();
      return generateInterview(app, {
        interviewerName: name,
        ...(role ? { interviewerRole: role } : {}),
        ...(type ? { interviewType: type } : {}),
      });
    },
    root,
    inputs,
  );
}

function hasGenerated(app: Application): boolean {
  return !!(app.generated && (app.generated.cv || app.generated.cover || app.generated.interview));
}

function renderGenerateBlock(app: Application, root: FileSystemDirectoryHandle): HTMLElement {
  const wallet = getWalletState();
  const walletNotice = !wallet.connection
    ? el(
        'div',
        { class: 'inline-banner warn' },
        el(
          'div',
          {},
          'Turn on AI in ',
          el('a', { href: '#/settings?focus=wallet' }, 'Settings'),
          ' to enable document generation.',
        ),
      )
    : null;

  // First-time encouragement: once AI is on but this job has no documents yet,
  // nudge the user toward generating their first one. Disappears after the first.
  const firstDocNudge =
    wallet.connection && !hasGenerated(app)
      ? el(
          'div',
          { class: 'inline-banner info' },
          el(
            'div',
            {},
            "You're one click from your first tailored document — pick CV or cover letter below and hit Generate. It compiles to a PDF right on your machine. ✨",
          ),
        )
      : null;

  return el(
    'div',
    { class: 'stack stack-sm' },
    el('div', { class: 'eyebrow' }, 'Generate'),
    walletNotice,
    firstDocNudge,
    renderGenerateSlot(
      app,
      'cv',
      'Tailored CV',
      'Generate',
      'AI rewrites your profile bullets to lead with what this job asks for, compiled to PDF locally with Typst.',
      () => generateCv(app),
      root,
    ),
    renderGenerateSlot(
      app,
      'cover',
      'Cover letter',
      'Generate',
      'A short, specific letter referencing the role and what you bring.',
      () => generateCover(app),
      root,
    ),
    renderInterviewSlot(app, root),
  );
}

export function renderJobDetail(opts: JobDetailOptions): HTMLElement {
  const { app, manifest, root } = opts;

  const statusSelect = el(
    'select',
    {
      class: 'status-select',
      'aria-label': 'Application status',
      onchange: async (e: Event) => {
        const next = (e.target as HTMLSelectElement).value as Status;
        try {
          await updateStatus(app.id, next);
        } catch (err) {
          alert(`Could not update status: ${err instanceof Error ? err.message : String(err)}`);
        }
      },
    },
    ...STATUSES.map((s) => el('option', { value: s, selected: s === app.status }, s)),
  );

  const statusPill = el(
    'label',
    {
      class: 'status-pill',
      style: 'padding: 0.5rem 0.9rem; font-size: 0.78rem;',
      // Wrapping the select in a <label> only focuses it on click — it
      // doesn't open the dropdown in Chromium. Call showPicker() so any
      // click inside the pill (dot, padding, chev) opens the menu.
      onclick: (e: Event) => {
        if (e.target === statusSelect) return;
        e.preventDefault();
        statusSelect.showPicker?.();
      },
    },
    el('span', { class: `dot ${statusClass(app.status)}` }),
    statusSelect,
    el('span', { class: 'chev' }, '▾'),
  );

  const filesList = el(
    'div',
    { class: 'stack stack-xs' },
    el('span', { class: 'text-faint', style: 'font-size: 0.75rem;' }, 'Scanning folder…'),
  );

  const descriptionBlock = el(
    'div',
    { class: 'job-description' },
    'Loading description…',
  );

  void (async () => {
    const txt = await readJobTxt(root, app);
    descriptionBlock.textContent = txt?.description?.trim() || '(No description recorded.)';
  })();

  void (async () => {
    const files: AppFiles = await detectFiles(root, app, manifest.config);
    if (files.all.length === 0) {
      filesList.replaceChildren(
        el(
          'span',
          { class: 'text-muted', style: 'font-size: 0.78rem; line-height: 1.55;' },
          'No files yet. Drop your CV / cover letter into the job folder, or use the generators below.',
        ),
      );
      return;
    }
    const buttons: HTMLElement[] = files.all.map((name) => {
      const isCv = files.cv === name;
      const isMot = files.motivation === name;
      const tag = isCv ? 'CV' : isMot ? 'Cover' : null;
      return el(
        'button',
        {
          class: 'btn tiny',
          style: 'justify-content: flex-start; align-items: flex-start; padding: 0.4rem 0.65rem; white-space: normal; text-align: left; word-break: break-word;',
          onclick: async () => {
            try { await openFile(root, app, name); }
            catch (err) { alert(`Could not open: ${err instanceof Error ? err.message : String(err)}`); }
          },
        },
        tag ? el('span', { class: 'text-accent' }, tag) : null,
        name,
      );
    });
    filesList.replaceChildren(...buttons);
  })();

  const removeBtn = el(
    'button',
    {
      class: 'btn danger',
      style: 'width: 100%; justify-content: center;',
      title: 'Remove from list (files on disk are kept)',
      onclick: async () => {
        if (!confirm(`Remove "${app.jobTitle}" from the list? Files on disk are kept.`)) return;
        try {
          await removeApplication(app.id);
          location.hash = '#/';
        } catch (err) {
          alert(`Could not remove: ${err instanceof Error ? err.message : String(err)}`);
        }
      },
    },
    'Remove from list',
  );

  const badge = isEasyApply(app.applyType)
    ? el('span', { class: 'apply-type-badge easy' }, 'Easy Apply')
    : el('span', { class: 'apply-type-badge external' }, 'External');

  const openPostingBtn = app.jobUrl
    ? el(
        'a',
        {
          href: app.jobUrl,
          target: '_blank',
          rel: 'noreferrer',
          class: 'btn compact',
          title: app.jobUrl,
        },
        'Open posting ↗',
      )
    : null;

  // Filtered jobs carry the auto-screen rejection reason in notes — surface it
  // up top so opening one explains why it was set aside.
  const filteredNotice =
    app.status === 'Filtered' && app.notes
      ? el(
          'div',
          { class: 'inline-banner warn' },
          el(
            'div',
            {},
            el('strong', {}, 'Filtered out — '),
            app.notes.replace(/^Auto-screen:\s*/i, ''),
          ),
        )
      : null;

  return el(
    'section',
    { class: 'page' },
    el(
      'div',
      {},
      el('a', { class: 'back-link', href: '#/' }, '← Dashboard'),
    ),
    filteredNotice,
    el(
      'header',
      { class: 'job-head' },
      el('div', { class: 'company' }, app.companyName),
      el('h1', {}, app.jobTitle),
      el(
        'div',
        { class: 'meta' },
        badge,
        el('span', { class: 'text-muted', style: 'font-size: 0.72rem;' }, `Saved ${fmtDate(app.savedAt)}`),
        openPostingBtn,
      ),
    ),
    el(
      'div',
      { class: 'job-shell' },
      el(
        'div',
        { class: 'job-body' },
        el('div', { class: 'eyebrow' }, 'Job description'),
        descriptionBlock,
      ),
      el(
        'aside',
        { class: 'job-sidebar' },
        el(
          'div',
          { class: 'card padded-sm stack stack-xs' },
          el('div', { class: 'field-label' }, 'Status'),
          statusPill,
        ),
        el(
          'div',
          { class: 'card padded-sm stack stack-xs' },
          el('div', { class: 'field-label' }, 'Files in folder'),
          filesList,
        ),
        renderGenerateBlock(app, root),
        el('div', {}, removeBtn),
      ),
    ),
  );
}
